import React, { useState } from "react";
import { useHealthCheck } from "../hooks/useHealthCheck";
import { useAuth } from "../contexts/AuthContext";
import { TabType } from "../types/api";
import HealthPanel from "./HealthPanel";
import ConfigurationPanel from "./ConfigurationPanel";
import DataProcessingPanel from "./DataProcessingPanel";
import LogQueryPanel from "./LogQueryPanel";
import AppLogsPanel from "./AppLogsPanel";
import LookupManagementPanel from "./LookupManagementPanel";
import ResultsBrowsingPanel from "./ResultsBrowsingPanel";
import UnprocessedPanel from "./UnprocessedPanel";
import HorseNamingPanel from "./HorseNamingPanel";
import "../styles/Dashboard.css";

const Dashboard: React.FC = () => {
    const { health, loading: healthLoading, error: healthError, refetch: refreshHealth } = useHealthCheck();
    const { currentUser, logout } = useAuth();
    const [activeTab, setActiveTab] = useState<TabType>("health");

    const getStatusClass = () => {
        if (healthLoading) return "loading";
        if (healthError || !health) return "unhealthy";
        return health.status;
    };

    const getStatusText = () => {
        if (healthLoading) return "Checking...";
        if (healthError) return "Error";
        if (!health) return "Unknown";
        return health.status;
    };

    const renderTabContent = () => {
        switch (activeTab) {
            case "health":
                return <HealthPanel health={health} loading={healthLoading} error={healthError} onRefresh={refreshHealth} />;
            case "config":
                return <ConfigurationPanel />;
            case "process":
                return <DataProcessingPanel />;
            case "logs":
                return <LogQueryPanel />;
            case "app-logs":
                return <AppLogsPanel />;
            case "lookup":
                return <LookupManagementPanel />;
            case "results":
                return <ResultsBrowsingPanel />;
            case "unprocessed":
                return <UnprocessedPanel />;
            case "horse-naming":
                return <HorseNamingPanel />;
            default:
                return <div>Select a tab to get started</div>;
        }
    };

    return (
        <div className="ras-dashboard">
            <header className="dashboard-header">
                <h1>RAS Data Feed Dashboard</h1>
                <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
                    <div className={`status-indicator ${getStatusClass()}`}>
                        {healthLoading && <span className="loading-spinner" />}
                        {getStatusText()}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <span style={{ fontSize: '14px', color: '#6c757d' }}>
                            Welcome, <strong>{currentUser}</strong>
                        </span>
                        <button
                            onClick={logout}
                            style={{
                                padding: '6px 12px',
                                fontSize: '12px',
                                backgroundColor: '#dc3545',
                                color: 'white',
                                border: 'none',
                                borderRadius: '4px',
                                cursor: 'pointer',
                                transition: 'background-color 0.2s ease'
                            }}
                            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#c82333'}
                            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#dc3545'}
                            title="Sign out"
                        >
                            Logout
                        </button>
                    </div>
                </div>
            </header>

            <nav className="dashboard-nav">
                <button className={`nav-button ${activeTab === "health" ? "active" : ""}`} onClick={() => setActiveTab("health")}>
                    System Health
                </button>
                <button className={`nav-button ${activeTab === "unprocessed" ? "active" : ""}`} onClick={() => setActiveTab("unprocessed")}>
                    Unprocessed
                </button>
                <button className={`nav-button ${activeTab === "results" ? "active" : ""}`} onClick={() => setActiveTab("results")}>
                    Results Browsing
                </button>
                <button className={`nav-button ${activeTab === "logs" ? "active" : ""}`} onClick={() => setActiveTab("logs")}>
                    Logs
                </button>
                <button className={`nav-button ${activeTab === "app-logs" ? "active" : ""}`} onClick={() => setActiveTab("app-logs")}>
                    App Logs
                </button>
                <button className={`nav-button ${activeTab === "lookup" ? "active" : ""}`} onClick={() => setActiveTab("lookup")}>
                    Lookup Management
                </button>
                <button className={`nav-button ${activeTab === "horse-naming" ? "active" : ""}`} onClick={() => setActiveTab("horse-naming")}>
                    Horse Naming
                </button>
                <button className={`nav-button ${activeTab === "process" ? "active" : ""}`} onClick={() => setActiveTab("process")}>
                    Data Processing
                </button>
                <button className={`nav-button ${activeTab === "config" ? "active" : ""}`} onClick={() => setActiveTab("config")}>
                    ⚙️ Configuration
                </button>
            </nav>

            <main className="dashboard-main">{renderTabContent()}</main>
        </div>
    );
};

export default Dashboard;
